import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeMosaicOutlinedIcon from '@mui/icons-material/AutoAwesomeMosaicOutlined';
import EventIcon from '@mui/icons-material/Event';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Grid, DialogContent, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import {
  INvoiceFile,
  saveInvoiceFile,
  IFile,
  getClientInvoiceInfo,
  getClientInvoiceFileList,
  saveActivityLog,
  LogEntityType,
  LogAction,
  LogContextType,
} from 'wisestep-api';
import {
  ActiveJob,
  WsTypography,
  WsButton,
  WsIconProfile,
  WsColors,
  WsIconViewJD,
  WsDrawer,
  ControlledTextInput,
  getFormatDate,
} from 'wisestep-base';

import { activityLogDTOHelper } from '../../../features/activityLog/ActivityHelper';
import UploadFile from '../../../features/clientDetails/components/uploadFile/UploadFile';
import { useInvoicingAuthz, InvoicingAction } from '../../../hooks/useInvoicingAuthz';
import ErrorLogger from '../../../providers/ErrorLogger';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { downloadCV } from '../../conversationWidget/utils/cvHelper';
import { defaultClientInvocieFilePayload, IDrawerDocument } from '../helper/InvocieSettingsHelper';

import InvoiceDetailsCard, { InvoiceDetailsCardAttribute, properties } from './InvoiceDetailsCard';

const OtherDocuments = ({ invoiceId }: IDrawerDocument) => {
  const methods = useForm();
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState<boolean>(false);
  const [invocieFileDetails, setInvocieFileDetails] = useState<INvoiceFile[]>([]);
  const [clientId, setclientId] = useState<string>();
  const [refresh, setRefresh] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [flag, setFlag] = useState<boolean>(false);

  const isAuthorizedAddDocument = useInvoicingAuthz(
    InvoicingAction.ADD_INVOICE_DETAILS_ADD_DOCUMENTS
  );

  useEffect(() => {
    const fetchInvoiceInfo = async () => {
      const res = await getClientInvoiceInfo(invoiceId);
      setclientId(res.data.clientId);
    };
    fetchInvoiceInfo();
    const fetchDataInvoiceFileDetails = async () => {
      const res = await getClientInvoiceFileList(invoiceId);
      setInvocieFileDetails(res.data);
    };

    fetchDataInvoiceFileDetails();
  }, [setNotification, invoiceId, flag]);

  const handleDialog = (value: boolean) => setOpenUploadFileDialog(value);

  const saveFile = async (file: IFile) => {
    const payload = { ...defaultClientInvocieFilePayload };
    const avPayload = { ...activityLogDTOHelper };

    try {
      if (file.location) {
        payload.clientId = clientId ?? '';
        payload.invoiceId = invoiceId;
        file.location = file.location.split('?')[0];
        payload.file = file;
        payload.documentName = methods.getValues('docName') ?? '';
        payload.documentDescription = methods.getValues('docDes') ?? '';
        const res = await saveInvoiceFile(payload);
        avPayload.action = LogAction.Add;
        avPayload.entityType = LogEntityType.OtherDocuments;
        avPayload.parentEntitiyType = LogContextType.Invoice;
        avPayload.description = `ADDED ${payload.documentName} document under Invoice`;
        avPayload.parentEntitiyId = invoiceId;
        avPayload.entityId = res.data.id;
        avPayload.field = `Other Documents`;
        avPayload.entityName = payload.documentName;
        avPayload.contextData = { invoiceId: invoiceId };
        await saveActivityLog(avPayload);
        setFlag(!flag);
        setRefresh(!refresh);
        handleDialog(false);
        setNotification(success('File has been saved successfully !!'));
      }
    } catch {
      setNotification(failed('Failed to save file, Please try again !!'));
    }
  };

  return (
    <>
      {isAuthorizedAddDocument && (
        <Grid item xs={12} pt={2} pb={2} container justifyContent={'flex-end'}>
          <WsButton
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => setOpenUploadFileDialog(true)}
          >
            Upload Document
          </WsButton>
        </Grid>
      )}

      {invocieFileDetails.map((data, index) => (
        <Box key={index} pt={2}>
          <InvoiceDetailsCard
            key={index}
            icon={<ActiveJob />}
            title="Other Documents"
            right={
              <FileDownloadOutlinedIcon
                sx={{ color: WsColors.GreyBlack }}
                onClick={() => downloadCV(data.file.location, data.file.name)}
              />
            }
          >
            <InvoiceDetailsCardAttribute columns={4}>
              <EventIcon />
              <WsTypography {...properties}>{getFormatDate(String(data?.createdOn))}</WsTypography>
            </InvoiceDetailsCardAttribute>
            <InvoiceDetailsCardAttribute columns={3}>
              <WsIconViewJD />
              <WsTypography>{data.documentName}</WsTypography>
            </InvoiceDetailsCardAttribute>
            <InvoiceDetailsCardAttribute columns={3}>
              <WsIconProfile color={WsColors.GreyBlack} />
              <WsTypography {...properties}>{data.createdByName}</WsTypography>
            </InvoiceDetailsCardAttribute>
            <InvoiceDetailsCardAttribute columns={6}>
              <AutoAwesomeMosaicOutlinedIcon />
              <WsTypography {...properties}>{data.documentDescription}</WsTypography>
            </InvoiceDetailsCardAttribute>
          </InvoiceDetailsCard>
        </Box>
      ))}
      {openUploadFileDialog && (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <WsDrawer
            open={openUploadFileDialog}
            size="small"
            onClose={() => handleDialog(false)}
            title="Upload Document"
          >
            <FormProvider {...methods}>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <ControlledTextInput
                      name="docName"
                      type="text"
                      fullWidth
                      id="outlined-basic"
                      placeholder="Document Name"
                      variant="outlined"
                      labelText="Document Name"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextInput
                      name="docDes"
                      type="text"
                      fullWidth
                      id="outlined-basic"
                      placeholder="Document Description"
                      variant="outlined"
                      labelText="Document Description"
                      size="small"
                    />
                  </Grid>
                </Grid>
                <UploadFile onCancel={() => handleDialog(false)} onContinue={saveFile} />
              </DialogContent>
            </FormProvider>
          </WsDrawer>
        </ErrorBoundary>
      )}
    </>
  );
};

export default OtherDocuments;
